import Cookies from "js-cookie";
import clientSide from "~/helpers/clientSide";
import { setCookie } from "~/helpers/cookie";

// OneTrust types for consent management
interface OneTrustGroup {
  CustomGroupId: string;
  Status: string;
}

interface OneTrustAPI {
  GetDomainData?: () => {
    Groups: OneTrustGroup[];
  };
}

interface WindowWithOneTrust extends Window {
  OneTrust?: OneTrustAPI;
  OnetrustActiveGroups?: string;
}

export interface ReturnVisitorCookie {
  env: string; // Region (FTR-sourced, returned after serviceability)
  controlNumber: string; // Address ID (FTR-sourced, returned after serviceability)
  sessionId: string; // cohesion session id
  shownReturnVisitorOffer?: boolean; // Whether user has been shown the Return Visitor Offer popup
}

const COOKIE_NAME = "x-cohort-return-visitor";

/**
 * Check if the current environment is localhost
 * Extracted as a reusable helper since this check is used in multiple places
 */
const isLocalhost = (): boolean => {
  return clientSide && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
};

/**
 * Set to true and enable to bypass OneTrust consent check
 */
const BYPASS_ONETRUST_CONSENT_CHECK = true;

/**
 * Check if marketing consent is granted via OneTrust
 */
export const hasMarketingConsent = (): boolean => {
  // TEMPORARY: Bypass consent check for testing
  if (BYPASS_ONETRUST_CONSENT_CHECK) {
    return true;
  }

  // Check if we're in a client-side environment
  if (typeof window === "undefined") {
    return false;
  }

  try {
    // OneTrust consent groups - typically C0002 is for marketing/targeting cookies
    // You may need to adjust this group ID based on your OneTrust configuration
    const marketingGroupId = "C0002";

    // Use window.OnetrustActiveGroups, which is a comma-separated list of the active categories.
    // If "C0002" (marketingGroupId) is included, we have consent.
    // See: https://onetrust-pub.site.com/s/article/UUID/How-does-OnetrustActiveGroups-work
    const windowWithOneTrust = window as WindowWithOneTrust;
    const onetrustActiveGroups = windowWithOneTrust?.OnetrustActiveGroups;

    if (onetrustActiveGroups) {
      const hasConsent = onetrustActiveGroups.includes(marketingGroupId);

      // Override for testing in dev/localhost
      const localhost = isLocalhost();
      if (!hasConsent && localhost) {
        return true;
      }

      return hasConsent;
    }
    return false;

    // If OneTrust is not loaded or consent not determined, default to true for local dev
  } catch (error) {
    console.error("Error checking marketing consent:", error);
    // Default to false for privacy compliance
    return false;
  }
};

/**
 * Set the returnVisitor cookie
 */
export const setReturnVisitorCookie = (data: Partial<ReturnVisitorCookie>): void => {
  // Check if we're overwriting an existing cookie
  const existingCookie = Cookies.get(COOKIE_NAME);

  if (!hasMarketingConsent()) {
    return;
  }

  // Preserve existing shownReturnVisitorOffer value if cookie exists
  const existingData = existingCookie ? JSON.parse(existingCookie) : {};

  if (existingData?.sessionId) {
    delete existingData.sessionId;
  }

  const cohesionSessionId =
    typeof window !== "undefined" && window?._Cohesion?.sessionId
      ? window?._Cohesion.sessionId
      : "undefined-session-id";

  const cookieData: ReturnVisitorCookie = {
    env: "",
    controlNumber: "",
    sessionId: cohesionSessionId,
    shownReturnVisitorOffer: false, // Default for new cookies
    ...existingData, // Preserve existing values (including shownReturnVisitorOffer)
    ...data,
  };

  try {
    setCookie(COOKIE_NAME, JSON.stringify(cookieData), { otGroup: "C0003" });
  } catch (error) {
    console.error("Failed to set return visitor cookie:", error);
  }
};

/**
 * Get the returnVisitor cookie
 */
export const getReturnVisitorCookie = (): ReturnVisitorCookie | null => {
  if (!hasMarketingConsent()) {
    return null;
  }

  try {
    const cookieValue = Cookies.get(COOKIE_NAME);
    if (!cookieValue) {
      return null;
    }

    const parsed = JSON.parse(cookieValue) as ReturnVisitorCookie;

    // Validate cookie structure
    if (!parsed.sessionId) {
      removeReturnVisitorCookie();
      return null;
    }

    return parsed;
  } catch (error) {
    console.error("Failed to parse return visitor cookie:", error);
    // Remove corrupted cookie
    removeReturnVisitorCookie();
    return null;
  }
};

/**
 * Remove the returnVisitor cookie
 */
export const removeReturnVisitorCookie = (): void => {
  const isProduction = process.env.environment === "production";

  const removeOptions = {
    path: "/",
    domain: isProduction ? ".frontier.com" : undefined,
  };

  Cookies.remove(COOKIE_NAME, removeOptions);
};

/**
 * Check if a return visitor cookie exists and is valid
 */
export const hasValidReturnVisitorCookie = (): boolean => {
  const cookie = getReturnVisitorCookie();
  return cookie !== null;
};

/**
 * Update the return visitor cookie to mark that the offer has been shown
 * Call this when the Return Visitor Offer popup is displayed
 *
 */
export const markReturnVisitorOfferShown = (): void => {
  if (!clientSide) return;

  // Check for debug mode via URL parameter
  const isDebugMode =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debugRVO") === "true";

  const existingCookie = getReturnVisitorCookie();
  if (!existingCookie) {
    console.error("Cannot mark offer as shown: no return visitor cookie exists");
    return;
  }

  const updatedCookie: ReturnVisitorCookie = {
    ...existingCookie,
    shownReturnVisitorOffer: true,
  };

  try {
    // In debug mode, bypass marketing consent check for cookie updates
    if (isDebugMode) {
      setCookie(COOKIE_NAME, JSON.stringify(updatedCookie), { otGroup: "C0003" });
    } else {
      setCookie(COOKIE_NAME, JSON.stringify(updatedCookie), { otGroup: "C0003" });
    }
  } catch (error) {
    console.error("Failed to update return visitor cookie:", error);
  }
};

/**
 * Check if RVO modal was shown on the MLP's in THIS session
 * MLP's should set this sessionStorage flag when they show the RVO modal
 * This is session specific, so it only prevents double-modal within same session
 */
export const wasRVOShownOnLandingPage = (): boolean => {
  if (!clientSide) return false;

  try {
    // Check if FRDC/MLP set the flag when they showed the modal
    return window.sessionStorage.getItem("rvoShownOnLandingPage") === "true";
  } catch (error) {
    return false;
  }
};
